import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import vocabulary from '../../constants/microObservations.json';
import Button from '../ui/Button';

/*
 * What a person saw on one micrograph (microscopy_observations, migration 0111;
 * routes/microscopy_observations.py).
 *
 * The current observation first, with who and when; a form that starts from it,
 * since saving adds a new one and never edits; the history, withdrawn ones
 * marked. A group left out says nothing. "Not seen" is an answer of its own: a
 * clamp not seen and a clamp absent are different evidence.
 *
 * Recording waits for the mycologist's sign-off, which the server reports
 * (`signed_off`): until then the form isn't offered, and the panel says why. A
 * provisional sign-off opens the form for testing, and the panel says that too.
 * Answers here change no AI score. A caller who can't read the lab's records
 * (403) sees no panel at all.
 *
 * On a layer of a Z-stack (L11) an observation belongs to that one image, so the
 * panel says which layer it is on and lists what was seen on the others, each
 * naming its layer. `onOpenLayer` adds a way to that layer; `onDirty` tells the
 * caller a form has words in it that aren't saved.
 */
const NO_DRAFT = { tags: {}, widthMin: '', widthMax: '', septaMin: '', septaMax: '', notes: '', specimen: '' };

function draftFrom(current) {
  if (!current) return NO_DRAFT;
  const tags = {};
  current.character_tags.forEach((tag) => {
    const [group, value] = tag.split(':');
    tags[group] = [...(tags[group] || []), value];
  });
  const text = v => (v === null || v === undefined ? '' : String(v));
  return { tags, widthMin: text(current.hypha_width_min_um), widthMax: text(current.hypha_width_max_um),
           septaMin: text(current.septa_min), septaMax: text(current.septa_max), notes: current.notes || '',
           specimen: current.specimen_id || '' };
}

const number = v => (v === '' ? undefined : Number(v));

function bodyOf(draft) {
  const body = {
    character_tags: vocabulary.groups.flatMap(g => (draft.tags[g.key] || []).map(v => `${g.key}:${v}`)),
    hypha_width_min_um: number(draft.widthMin), hypha_width_max_um: number(draft.widthMax),
    septa_min: number(draft.septaMin), septa_max: number(draft.septaMax),
    notes: draft.notes.trim() || undefined, specimen_id: draft.specimen || undefined,
  };
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined));
}

function Summary({ t, row, children }) {
  const range = (low, high) => (low === high || high === null ? low : low === null ? high : `${low}–${high}`);
  const width = range(row.hypha_width_min_um, row.hypha_width_max_um);
  const septa = range(row.septa_min, row.septa_max);
  return (
    <div data-testid="observation-row" data-withdrawn={row.withdrawn ? 'true' : 'false'}
         style={{ fontSize: 13, opacity: row.withdrawn ? 0.65 : 1 }}>
      <ul style={chipsStyle}>
        {row.character_tags.map((tag) => {
          const [group, value] = tag.split(':');
          return <li key={tag} style={chipStyle}>{t(`observation.${group}._label`)}: {t(`observation.${group}.${value}`)}</li>;
        })}
      </ul>
      {width !== null && <div>{t('observation.width')}: <bdi dir="ltr">{width} µm</bdi></div>}
      {septa !== null && <div>{t('observation.septa')}: <bdi dir="ltr">{septa}</bdi></div>}
      {row.notes && <div>{row.notes}</div>}
      <small style={mutedStyle}>
        {row.created_by_name} · <bdi dir="ltr">{(row.created_at || '').slice(0, 10)}</bdi>
      </small>
      {row.withdrawn && (
        <div data-testid="observation-withdrawn" style={mutedStyle}>
          {t('observation.withdrawnBy', { name: row.withdrawn_by_name || '', reason: row.withdrawal_reason || '' })}
        </div>
      )}
      {children}
    </div>
  );
}

export default function ObservationPanel({ imageId, umPerPx = null, readOnly = false, isolates = [], titled = true,
                                           bundleId = null, onOpenLayer = null, onDirty = null }) {
  const { t } = useTranslation('scan');
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [draft, setDraft] = useState(NO_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [withdrawing, setWithdrawing] = useState(null);
  const [reason, setReason] = useState('');
  const [layers, setLayers] = useState(null);

  const loadLayers = useCallback(() => {
    if (!bundleId) return Promise.resolve();
    return Promise.resolve()
      .then(() => api.get(`/media-bundles/${bundleId}/observations`))
      .then(({ data: read }) => setLayers(Array.isArray(read?.layers) ? read.layers : null))
      .catch(() => setLayers(null));
  }, [bundleId]);

  const load = useCallback(() => Promise.resolve()
    .then(() => api.get(`/microscopy/${imageId}/observations`))
    .then(({ data: read }) => {
      if (!Array.isArray(read?.observations)) { setHidden(true); return; }
      setData(read);
      setDraft(draftFrom(read.current));
    })
    .catch(() => setHidden(true)), [imageId]);
  useEffect(() => { setHidden(false); setData(null); load(); }, [load]);
  useEffect(() => { loadLayers(); }, [loadLayers]);

  const dirty = !!data && !readOnly && JSON.stringify(draft) !== JSON.stringify(draftFrom(data.current));
  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);
  useEffect(() => () => { onDirty?.(false); }, [onDirty]);

  if (hidden || !data) return null;

  const act = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      setWithdrawing(null);
      setReason('');
      await Promise.all([load(), loadLayers()]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const chooseOne = (group, value) => setDraft(d => ({ ...d, tags: { ...d.tags, [group]: value ? [value] : [] } }));
  const toggle = (group, value) => setDraft((d) => {
    const had = d.tags[group] || [];
    return { ...d, tags: { ...d.tags, [group]: had.includes(value) ? had.filter(v => v !== value) : [...had, value] } };
  });
  const set = key => e => setDraft(d => ({ ...d, [key]: e.target.value }));
  const body = bodyOf(draft);
  const canRecord = !readOnly && !!data.signed_off;
  const others = data.observations.filter(o => o.observation_id !== data.current?.observation_id);
  const here = layers?.find(l => l.image_id === imageId);
  const elsewhere = (layers || []).filter(l => l.image_id !== imageId && l.current);

  return (
    <section aria-label={t('observation.title')} data-testid="observation-panel" style={sectionStyle}>
      {titled && <strong style={{ fontSize: 14 }}>{t('observation.title')}</strong>}
      {here && (
        <p role="note" data-testid="observation-layer" data-layer={here.position} style={mutedStyle}>
          {t('observation.onLayer', { n: here.position + 1, count: layers.length })}
        </p>
      )}
      {data.current ? (
        <div data-testid="observation-current">
          <Summary t={t} row={data.current}>
            {canRecord && withdrawing !== data.current.observation_id && (
              <Button variant="ghost" data-testid="observation-withdraw"
                      onClick={() => setWithdrawing(data.current.observation_id)}>{t('observation.withdraw')}</Button>
            )}
          </Summary>
          {withdrawing === data.current.observation_id && (
            <div style={rowStyle}>
              <label style={fieldStyle}>
                <span>{t('reason')}</span>
                <input value={reason} onChange={e => setReason(e.target.value)} style={inputStyle}
                       data-testid="observation-withdraw-reason" />
              </label>
              <Button variant="danger" disabled={reason.trim().length < 3 || busy} data-testid="observation-withdraw-confirm"
                      onClick={() => act(() => api.post(`/microscopy/${imageId}/observations/${withdrawing}/withdraw`,
                                                        { reason: reason.trim() }))}>
                {t('observation.withdraw')}
              </Button>
              <Button variant="ghost" onClick={() => setWithdrawing(null)}>{t('observation.keep')}</Button>
            </div>
          )}
        </div>
      ) : <p style={mutedStyle} data-testid="observation-none">{t('observation.none')}</p>}

      {!data.signed_off && !readOnly && (
        <p role="note" data-testid="observation-awaiting" style={mutedStyle}>{t('observation.awaiting')}</p>
      )}
      {canRecord && data.signed_off.provisional && (
        <p role="note" data-testid="observation-provisional" style={mutedStyle}>{t('observation.provisional')}</p>
      )}
      {canRecord && (
        <div data-testid="observation-form" style={formStyle}>
          <p style={mutedStyle}>{t('observation.help')}</p>
          {vocabulary.groups.map(group => (group.max === 1 ? (
            <label key={group.key} style={fieldStyle}>
              <span>{t(`observation.${group.key}._label`)}</span>
              <select value={(draft.tags[group.key] || [])[0] || ''} style={inputStyle}
                      data-testid={`observation-${group.key}`} onChange={e => chooseOne(group.key, e.target.value)}>
                <option value="">{t('observation.unsaid')}</option>
                {group.values.map(v => <option key={v} value={v}>{t(`observation.${group.key}.${v}`)}</option>)}
              </select>
            </label>
          ) : (
            <fieldset key={group.key} style={fieldsetStyle}>
              <legend style={{ fontSize: 13 }}>{t(`observation.${group.key}._label`)}</legend>
              {group.values.map(v => (
                <label key={v} style={checkStyle}>
                  <input type="checkbox" checked={(draft.tags[group.key] || []).includes(v)}
                         data-testid={`observation-${group.key}-${v}`} onChange={() => toggle(group.key, v)} />
                  <span>{t(`observation.${group.key}.${v}`)}</span>
                </label>
              ))}
            </fieldset>
          )))}
          <div style={rowStyle}>
            <label style={fieldStyle}>
              <span>{t('observation.widthMin')}</span>
              <input type="number" min={0} step="any" value={draft.widthMin} onChange={set('widthMin')} disabled={!umPerPx}
                     style={numberStyle} data-testid="observation-width-min" dir="ltr" />
            </label>
            <label style={fieldStyle}>
              <span>{t('observation.widthMax')}</span>
              <input type="number" min={0} step="any" value={draft.widthMax} onChange={set('widthMax')} disabled={!umPerPx}
                     style={numberStyle} data-testid="observation-width-max" dir="ltr" />
            </label>
          </div>
          {!umPerPx && <small role="note" data-testid="observation-width-noscale" style={mutedStyle}>{t('observation.widthNeedsScale')}</small>}
          <div style={rowStyle}>
            <label style={fieldStyle}>
              <span>{t('observation.septaMin')}</span>
              <input type="number" min={0} step={1} value={draft.septaMin} onChange={set('septaMin')} style={numberStyle}
                     data-testid="observation-septa-min" dir="ltr" />
            </label>
            <label style={fieldStyle}>
              <span>{t('observation.septaMax')}</span>
              <input type="number" min={0} step={1} value={draft.septaMax} onChange={set('septaMax')} style={numberStyle}
                     data-testid="observation-septa-max" dir="ltr" />
            </label>
          </div>
          {isolates.length > 0 && (
            <label style={fieldStyle}>
              <span>{t('observation.isolate')}</span>
              <select value={draft.specimen} onChange={set('specimen')} style={inputStyle} data-testid="observation-isolate">
                <option value="">{t('observation.noIsolate')}</option>
                {isolates.map(i => (
                  <option key={i.specimen_id} value={i.specimen_id}>
                    {[i.lab_code, i.specimen_code, i.taxon_name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label style={fieldStyle}>
            <span>{t('observation.notes')}</span>
            <textarea value={draft.notes} onChange={set('notes')} rows={2} maxLength={2000} style={inputStyle}
                      data-testid="observation-notes" />
          </label>
          <div style={rowStyle}>
            <Button variant="primary" disabled={!body.character_tags.length || busy} data-testid="observation-save"
                    onClick={() => act(() => api.post(`/microscopy/${imageId}/observations`, body))}>
              {t('observation.save')}
            </Button>
          </div>
          {!body.character_tags.length && <small style={mutedStyle}>{t('observation.oneNeeded')}</small>}
        </div>
      )}
      {error && <p role="alert" style={{ fontSize: 13, margin: 0, color: 'var(--c-danger-fg)' }}>{error}</p>}

      {elsewhere.length > 0 && (
        <section aria-label={t('observation.otherLayers')} data-testid="observation-other-layers" style={formStyle}>
          <strong style={{ fontSize: 13 }}>{t('observation.otherLayers')}</strong>
          {elsewhere.map(layer => (
            <div key={layer.image_id} data-testid={`observation-layer-${layer.position}`} data-image={layer.image_id}>
              <strong style={{ fontSize: 13 }}>{t('observation.layerName', { n: layer.position + 1 })}</strong>
              <Summary t={t} row={layer.current}>
                {onOpenLayer && (
                  <Button variant="ghost" data-testid={`observation-go-${layer.position}`}
                          onClick={() => onOpenLayer(layer.image_id)}>
                    {t('observation.goToLayer', { n: layer.position + 1 })}
                  </Button>
                )}
              </Summary>
            </div>
          ))}
        </section>
      )}

      {others.length > 0 && (
        <details data-testid="observation-history">
          <summary style={{ fontSize: 13 }}>{t('observation.history')}</summary>
          <div style={formStyle}>{others.map(o => <Summary key={o.observation_id} t={t} row={o} />)}</div>
        </details>
      )}
    </section>
  );
}

const sectionStyle = { display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' };
const formStyle = { display: 'flex', flexDirection: 'column', gap: 8 };
const rowStyle = { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13 };
const checkStyle = { display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 };
const fieldsetStyle = { border: '1px solid var(--c-border)', borderRadius: 6, padding: '4px 8px', margin: 0 };
const inputStyle = { padding: '4px 6px', borderRadius: 6, border: '1px solid var(--c-border-strong)', maxWidth: '100%' };
const numberStyle = { ...inputStyle, width: 88 };
const mutedStyle = { fontSize: 13, margin: 0, color: 'var(--c-text-muted-fg)' };
const chipsStyle = { listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 4, padding: 0, margin: '0 0 4px' };
const chipStyle = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 10, padding: '1px 8px' };
